import type { PrivatePlayerAIHandoff } from "@playbookai/types";

const CHANNEL_PREFIX = "playbookai.player-ai.handoff.";
const HANDOFF_FRAGMENT_KEY = "handoff";
export const PRIVATE_HANDOFF_TTL_MS = 10_000;
export const PRIVATE_HANDOFF_RECEIVE_TIMEOUT_MS = 1_500;

type PrivateHandoffWireMessage =
  | Readonly<{ type: "ready"; handoff_id: string }>
  | Readonly<{
      type: "payload";
      handoff_id: string;
      payload: PrivatePlayerAIHandoff;
    }>
  | Readonly<{ type: "received"; handoff_id: string }>;

interface BroadcastChannelLike {
  onmessage: ((event: MessageEvent<PrivateHandoffWireMessage>) => void) | null;
  postMessage(message: PrivateHandoffWireMessage): void;
  close(): void;
}

interface PrivateHandoffBrowser {
  readonly origin: string;
  readonly pathname: string;
  readonly search: string;
  readonly hash: string;
  readonly open: (
    url: string,
    target: string,
    features: string,
  ) => Window | null;
  readonly replaceUrl: (url: string) => void;
  readonly createChannel: (name: string) => BroadcastChannelLike;
  readonly setTimer: (callback: () => void, delay: number) => number;
  readonly clearTimer: (timer: number) => void;
}

/**
 * Opens the destination synchronously from the user's gesture. Only a random
 * one-use rendezvous id enters the fragment; the entity and draft travel over
 * an ephemeral same-origin BroadcastChannel after the new tab says it is ready.
 */
export function openPrivatePlayerAIHandoff(
  handoff: PrivatePlayerAIHandoff,
  browser: PrivateHandoffBrowser = windowBrowser(),
): boolean {
  if (!validHandoffId(handoff.handoff_id)) {
    return openCleanPrivatePlayerAI(browser);
  }
  let channel: BroadcastChannelLike;
  try {
    channel = browser.createChannel(channelName(handoff.handoff_id));
  } catch {
    return openCleanPrivatePlayerAI(browser);
  }

  let settled = false;
  const finish = (): void => {
    if (settled) return;
    settled = true;
    browser.clearTimer(expiry);
    channel.close();
  };
  const expiry = browser.setTimer(finish, PRIVATE_HANDOFF_TTL_MS);
  channel.onmessage = (event) => {
    const message = event.data;
    if (message.handoff_id !== handoff.handoff_id) return;
    if (message.type === "ready") {
      channel.postMessage({
        type: "payload",
        handoff_id: handoff.handoff_id,
        payload: handoff,
      });
    } else if (message.type === "received") {
      finish();
    }
  };

  const destination = `${browser.origin}/player-ai#${HANDOFF_FRAGMENT_KEY}=${encodeURIComponent(handoff.handoff_id)}`;
  try {
    // `noopener` deliberately severs the opener and therefore makes the HTML
    // API return null even when the tab opened successfully. The rendezvous
    // channel and its TTL, not the return value, own delivery and cleanup.
    browser.open(destination, "_blank", "noopener");
    return true;
  } catch {
    finish();
    return false;
  }
}

export function receivePrivatePlayerAIHandoff(
  browser: PrivateHandoffBrowser = windowBrowser(),
): Promise<PrivatePlayerAIHandoff | null> {
  const handoffId = handoffIdFromHash(browser.hash);
  // Strip the rendezvous id before waiting. A reload or copied address always
  // becomes a clean composer and can never replay the payload.
  if (handoffId) browser.replaceUrl(`${browser.pathname}${browser.search}`);
  if (!handoffId) return Promise.resolve(null);

  let channel: BroadcastChannelLike;
  try {
    channel = browser.createChannel(channelName(handoffId));
  } catch {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    let settled = false;
    const finish = (payload: PrivatePlayerAIHandoff | null): void => {
      if (settled) return;
      settled = true;
      browser.clearTimer(expiry);
      channel.close();
      resolve(payload);
    };
    const expiry = browser.setTimer(
      () => finish(null),
      PRIVATE_HANDOFF_RECEIVE_TIMEOUT_MS,
    );
    channel.onmessage = (event) => {
      const message = event.data;
      if (
        message.type !== "payload" ||
        message.handoff_id !== handoffId ||
        message.payload.handoff_id !== handoffId ||
        message.payload.contract_version !== 1
      ) {
        return;
      }
      channel.postMessage({ type: "received", handoff_id: handoffId });
      finish(message.payload);
    };
    channel.postMessage({ type: "ready", handoff_id: handoffId });
  });
}

function openCleanPrivatePlayerAI(browser: PrivateHandoffBrowser): boolean {
  try {
    browser.open(`${browser.origin}/player-ai`, "_blank", "noopener");
    return true;
  } catch {
    return false;
  }
}

function handoffIdFromHash(hash: string): string | null {
  const value = new URLSearchParams(hash.replace(/^#/, "")).get(
    HANDOFF_FRAGMENT_KEY,
  );
  return value && validHandoffId(value) ? value : null;
}

function validHandoffId(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value,
  );
}

function channelName(handoffId: string): string {
  return CHANNEL_PREFIX + handoffId;
}

function windowBrowser(): PrivateHandoffBrowser {
  return {
    origin: window.location.origin,
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
    open: (url, target, features) => window.open(url, target, features),
    replaceUrl: (url) => window.history.replaceState(null, "", url),
    createChannel: (name) => new BroadcastChannel(name),
    setTimer: (callback, delay) => window.setTimeout(callback, delay),
    clearTimer: (timer) => window.clearTimeout(timer),
  };
}
